/**
 * assets/recharge-cart.js — the bridge to Recharge's cart, which this theme does not own.
 *
 * The store runs Recharge Storefront Experiences (static.rechargecdn.com/assets/
 * storefront-experiences/storefront-experiences.js, enabled as an app embed on the
 * published theme). That bundle owns the cart UI: a drawer, or a smaller "overlay"
 * confirmation, configured in the Recharge admin. It patches window.fetch and XMLHttp-
 * Request, so any POST to /cart/add publishes its own `cart:item:add`, which opens
 * whichever of the two the merchant has turned on. Read at build time from the bundle:
 *
 *   window.RechargeStorefrontConfig  { customer, shop, cart_overlay, cart_overlay_preview,
 *                                      cart_drawer, cart_drawer_preview, … }
 *   window.RechargePubSub            { subscribe, publish }
 *   topics it publishes              cart-drawer:open · cart-overlay:open · cart:item:add
 *                                    cart:item:update · cart:refresh · cart:update
 *   topics it subscribes to          cart:item:add · cart:item:update · cart:refresh
 *                                    cart:update  (and cart-drawer:open, in the drawer
 *                                    component itself)
 *
 * So the theme's job is only to (a) not navigate away before the drawer opens, which is
 * what /cart used to do, and (b) ask for it explicitly, since an add that Recharge's
 * patch somehow missed should still show the shopper their cart.
 *
 * THE CART PAGE IS NEVER USED, by instruction. The header's cart control keeps its
 * href for the no-JS case and for a storefront where Recharge is absent; wherever
 * Recharge is present this file intercepts the click and opens the drawer instead.
 * Recharge can also do that itself from a selector set in its admin ("cart trigger"),
 * and both paths are harmless together — opening an open drawer is a no-op.
 */

const config = () => window.RechargeStorefrontConfig;
const bus = () => window.RechargePubSub;

/** Whether Recharge's storefront cart is on this page at all. */
export function hasRechargeCart() {
  const c = config();
  const b = bus();
  if (!c || !b || typeof b.publish !== 'function') return false;
  return Boolean(c.cart_drawer || c.cart_drawer_preview || c.cart_overlay || c.cart_overlay_preview);
}

/**
 * Show the shopper their cart in Recharge's own UI. Returns false when Recharge is not
 * there, so the caller can fall back.
 *
 * The drawer opens on `cart-drawer:open` with no payload. The overlay needs the added
 * item and its variants, which only Recharge's own add hook has, so it is left to open
 * itself; publishing `cart:refresh` first means either one renders the cart as it is now.
 */
export function openRechargeCart() {
  if (!hasRechargeCart()) return false;
  const c = config();
  const b = bus();
  try {
    b.publish('cart:refresh');
    if (c.cart_drawer || c.cart_drawer_preview) b.publish('cart-drawer:open');
    return true;
  } catch {
    return false;
  }
}

// Any cart control on the page opens the drawer rather than loading /cart. `.header-cart`
// is the class the header already carries for the AJAX swap; `[data-cart-trigger]` is
// there for anything added later.
document.addEventListener('click', (event) => {
  const trigger = event.target.closest?.('[data-cart-trigger], .header-cart');
  if (!trigger || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
  if (!hasRechargeCart()) return;
  event.preventDefault();
  openRechargeCart();
});
